import "./LanguageSwitcher.scss";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  isSupportedLang,
  type SupportedLang,
  swapSlugForLang,
} from "../../config/routes";

interface LanguageSwitcherProps {
  mobile?: boolean;
  toggleNavbar?: () => void;
}

const LanguageSwitcher = ({
  mobile = false,
  toggleNavbar,
}: LanguageSwitcherProps) => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLanguage: SupportedLang = useMemo(() => {
    return lang === "fr" ? "fr" : "en";
  }, [lang]);

  const setLanguage = (newLang: SupportedLang) => {
    if (currentLanguage === newLang) return;

    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.length > 0 && isSupportedLang(segments[0])) {
      segments[0] = newLang;
    } else {
      segments.unshift(newLang);
    }

    // Swap localized slugs (e.g. sites-vitrines ↔ showcase)
    for (let i = 1; i < segments.length; i++) {
      segments[i] = swapSlugForLang(segments[i], newLang);
    }

    const nextPath = `/${segments.join("/")}`;
    void navigate(`${nextPath}${location.search}${location.hash}`);

    if (mobile && toggleNavbar) {
      toggleNavbar();
    }
  };

  return (
    <div
      className={`languageSwitcher ${mobile ? "mobile" : ""}`}
      role="group"
      aria-label={t("language.switcher_aria")}
    >
      <button
        type="button"
        className={currentLanguage === "en" ? "active" : ""}
        onClick={() => setLanguage("en")}
      >
        EN
      </button>
      <button
        type="button"
        className={currentLanguage === "fr" ? "active" : ""}
        onClick={() => setLanguage("fr")}
      >
        FR
      </button>
    </div>
  );
};

export default LanguageSwitcher;
